/** Build Favorites / Standard / Custom folder tree from report list API results. */

const slugify = (value) =>
  String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "") || "none";

const sortByName = (a, b) =>
  String(a.name || a.nameKey || "").localeCompare(String(b.name || b.nameKey || ""), undefined, {
    sensitivity: "base",
  });

export const getReportOwnerMeta = (report) => {
  const owner = report?.owner;
  if (owner == null || owner === "" || owner === "Public" || owner === "public") {
    return { ownerId: null, ownerName: null, isPublic: true };
  }
  if (typeof owner === "object") {
    const ownerId = owner.id ?? owner.user_id ?? null;
    const ownerName =
      owner.user_detail?.full_name ||
      owner.full_name ||
      owner.username ||
      owner.email ||
      null;
    if (!ownerId && !ownerName) {
      return { ownerId: null, ownerName: null, isPublic: true };
    }
    return {
      ownerId: ownerId != null ? String(ownerId) : slugify(ownerName),
      ownerName: ownerName || `User ${ownerId}`,
      isPublic: false,
    };
  }
  return {
    ownerId: slugify(owner),
    ownerName: String(owner),
    isPublic: false,
  };
};

export const getReportFolderName = (report) => {
  const linked = report?.report_folder;
  if (linked && typeof linked === "object" && linked.name) {
    return String(linked.name);
  }
  if (typeof linked === "string" && linked && linked !== "No Folder") {
    return linked;
  }
  const folder = report?.folder;
  if (folder == null || folder === "" || folder === "No Folder") return null;
  return String(folder);
};

/** Unique report-folder entities embedded on report list rows. */
export const extractFoldersFromReports = (reports = []) => {
  const map = new Map();
  reports.forEach((report) => {
    const folder = report?.report_folder;
    if (!folder || typeof folder !== "object") return;
    const id = folder.documentId ?? folder.id;
    if (id == null || id === "") return;
    const key = String(id);
    if (!map.has(key)) {
      map.set(key, {
        ...folder,
        id,
        documentId: folder.documentId ?? folder.id,
        name: folder.name,
        owner_folder: folder.owner_folder || report.owner || null,
        reports: [],
      });
    }
    map.get(key).reports.push({ id: report.id });
  });
  return Array.from(map.values());
};

const getOwnerMetaFromFolderEntity = (folder) => {
  const owner = folder?.owner_folder || folder?.owner;
  if (owner == null || owner === "") {
    return { ownerId: null, ownerName: null, isPublic: true, email: null, documentId: null };
  }
  if (typeof owner === "object") {
    const ownerId = owner.id ?? owner.documentId ?? owner.user_id ?? null;
    const ownerName =
      owner.user_detail?.full_name ||
      owner.full_name ||
      owner.username ||
      owner.email ||
      null;
    if (!ownerId && !ownerName) {
      return { ownerId: null, ownerName: null, isPublic: true, email: null, documentId: null };
    }
    return {
      ownerId: ownerId != null ? String(ownerId) : slugify(ownerName),
      ownerName: ownerName || `User ${ownerId}`,
      isPublic: false,
      email: owner.email ? String(owner.email).toLowerCase() : null,
      documentId: owner.documentId != null ? String(owner.documentId) : null,
    };
  }
  return {
    ownerId: String(owner),
    ownerName: String(owner),
    isPublic: false,
    email: null,
    documentId: null,
  };
};

const folderEntityId = (folder) => folder?.documentId ?? folder?.id ?? null;

/**
 * Build custom section children from report-folder entities (preferred).
 * Falls back to grouping reports by owner/folder string when folders list is empty.
 */
export const buildCustomOwnerTreeFromFolders = (folders = [], reports = []) => {
  if (!folders.length) {
    return buildCustomOwnerTree(reports);
  }

  const ownersMap = new Map();
  const emailToKey = new Map();
  const altIdToKey = new Map();

  const ensureOwner = (key, { ownerId, ownerName, isPublic }) => {
    if (!ownersMap.has(key)) {
      ownersMap.set(key, {
        id: isPublic ? "custom-owner-public" : `custom-owner-${ownerId}`,
        type: "owner",
        ownerId: isPublic ? null : ownerId,
        name: isPublic ? null : ownerName,
        nameKey: isPublic ? "reports.folders.publicOwner" : undefined,
        reportCount: 0,
        children: [],
      });
    } else if (ownerName && ownersMap.get(key).name !== ownerName) {
      const current = ownersMap.get(key).name || "";
      if (!current || current.includes("@")) {
        ownersMap.get(key).name = ownerName;
      }
    }
    return ownersMap.get(key);
  };

  reports.forEach((report) => {
    const meta = getReportOwnerMeta(report);
    const key = meta.isPublic ? "public" : String(meta.ownerId);
    ensureOwner(key, meta);
    ownersMap.get(key).reportCount += 1;

    const owner = report?.owner;
    if (owner && typeof owner === "object") {
      if (owner.email) emailToKey.set(String(owner.email).toLowerCase(), key);
      if (owner.documentId != null) altIdToKey.set(String(owner.documentId), key);
      if (owner.id != null) altIdToKey.set(String(owner.id), key);
    }
  });

  folders.forEach((folder) => {
    const meta = getOwnerMetaFromFolderEntity(folder);
    let key = meta.isPublic ? "public" : String(meta.ownerId);

    if (!ownersMap.has(key) && meta.email && emailToKey.has(meta.email)) {
      key = emailToKey.get(meta.email);
    }
    if (!ownersMap.has(key) && meta.documentId && altIdToKey.has(meta.documentId)) {
      key = altIdToKey.get(meta.documentId);
    }
    if (!ownersMap.has(key) && meta.ownerId && altIdToKey.has(String(meta.ownerId))) {
      key = altIdToKey.get(String(meta.ownerId));
    }

    const ownerNode = ensureOwner(key, meta);
    const id = folderEntityId(folder);
    if (id == null) return;

    if (ownerNode.children.some((child) => String(child.apiFolderId) === String(id))) {
      return;
    }

    const countFromRelation = Array.isArray(folder.reports) ? folder.reports.length : 0;
    const countFromReports = reports.filter((report) => {
      const rel = report?.report_folder;
      if (rel == null || rel === "") return false;
      if (typeof rel === "object") {
        return String(rel.id) === String(id) || String(rel.documentId) === String(id);
      }
      return String(rel) === String(id);
    }).length;

    ownerNode.children.push({
      id: `rf-${id}`,
      apiFolderId: id,
      name: folder.name || `Folder ${id}`,
      type: "folder",
      ownerId: ownerNode.ownerId,
      ownerName: ownerNode.name,
      reportCount: countFromReports || countFromRelation || 0,
      children: [],
    });
  });

  const owners = Array.from(ownersMap.values()).map((owner) => ({
    ...owner,
    children: owner.children.sort(sortByName),
  }));

  const publicOwner = owners.filter((o) => o.ownerId == null);
  const namedOwners = owners.filter((o) => o.ownerId != null).sort(sortByName);
  return [...publicOwner, ...namedOwners];
};

/**
 * Group custom reports: section → owner → folder (legacy string folder field)
 */
export const buildCustomOwnerTree = (reports = []) => {
  const ownersMap = new Map();

  reports.forEach((report) => {
    const { ownerId, ownerName, isPublic } = getReportOwnerMeta(report);
    const key = isPublic ? "public" : String(ownerId);
    if (!ownersMap.has(key)) {
      ownersMap.set(key, {
        id: isPublic ? "custom-owner-public" : `custom-owner-${ownerId}`,
        type: "owner",
        ownerId: isPublic ? null : ownerId,
        name: isPublic ? null : ownerName,
        nameKey: isPublic ? "reports.folders.publicOwner" : undefined,
        reportCount: 0,
        folders: new Map(),
      });
    }
    const ownerNode = ownersMap.get(key);
    ownerNode.reportCount += 1;

    const folderName = getReportFolderName(report);
    if (!folderName) return;

    const folderKey = slugify(folderName);
    if (!ownerNode.folders.has(folderKey)) {
      ownerNode.folders.set(folderKey, {
        id: `custom-folder-${key}-${folderKey}`,
        name: folderName,
        type: "folder",
        ownerId: ownerNode.ownerId,
        ownerName: ownerNode.name,
        reportCount: 0,
        children: [],
      });
    }
    ownerNode.folders.get(folderKey).reportCount += 1;
  });

  const owners = Array.from(ownersMap.values()).map((owner) => ({
    id: owner.id,
    type: "owner",
    ownerId: owner.ownerId,
    name: owner.name,
    nameKey: owner.nameKey,
    reportCount: owner.reportCount,
    children: Array.from(owner.folders.values()).sort(sortByName),
  }));

  const publicOwner = owners.filter((o) => o.ownerId == null);
  const namedOwners = owners.filter((o) => o.ownerId != null).sort(sortByName);
  return [...publicOwner, ...namedOwners];
};

/**
 * Group standard reports by folder name
 */
export const buildStandardFolderTree = (reports = []) => {
  const foldersMap = new Map();

  reports.forEach((report) => {
    const folderName = getReportFolderName(report) || report?.name || "General";
    const folderKey = slugify(folderName);
    if (!foldersMap.has(folderKey)) {
      foldersMap.set(folderKey, {
        id: `std-folder-${folderKey}`,
        name: folderName,
        type: "folder",
        reportCount: 0,
        children: [],
      });
    }
    foldersMap.get(folderKey).reportCount += 1;
  });

  return Array.from(foldersMap.values()).sort(sortByName);
};

/**
 * @param {{
 *   allowedTabs?: string[],
 *   customReports?: array,
 *   standardReports?: array,
 *   recentReports?: array,
 *   folderEntities?: array,
 * }} opts
 */
export const buildReportFoldersTree = ({
  allowedTabs = ["favorites", "standard", "custom"],
  customReports = [],
  standardReports = [],
  recentReports = [],
  folderEntities = [],
} = {}) => {
  const sections = [];
  const recentSection = Array.isArray(recentReports)
    ? {
        id: "recent-root",
        nameKey: "reports.folders.recent",
        type: "section",
        tab: null,
        children: recentReports.slice(0, 5).map((report) => ({
          id: `recent-${report.id}`,
          reportId: report.id,
          name: report.name || `Report ${report.id}`,
          type: "recent-report",
          reportType: report.type || null,
          children: [],
        })),
      }
    : null;

  if (allowedTabs.includes("favorites")) {
    sections.push({
      id: "favorites-root",
      nameKey: "reports.folders.favorites",
      type: "section",
      tab: "favorites",
      children: [],
    });
  }

  if (allowedTabs.includes("standard")) {
    sections.push({
      id: "standard-root",
      nameKey: "reports.folders.standard",
      type: "section",
      tab: "standard",
      children: [],
    });
  }

  if (allowedTabs.includes("custom")) {
    sections.push({
      id: "custom-root",
      nameKey: "reports.folders.custom",
      type: "section",
      tab: "custom",
      reportCount: customReports.length,
      children: buildCustomOwnerTreeFromFolders(folderEntities, customReports),
    });
  }

  if (allowedTabs.includes("schedule")) {
    const scheduledCount = [...customReports, ...standardReports].filter(
      (report) => report?.has_schedule
    ).length;
    sections.push({
      id: "schedule-root",
      nameKey: "reports.folders.schedule",
      type: "section",
      tab: "schedule",
      reportCount: scheduledCount,
      children: [],
    });
  }

  if (recentSection) {
    sections.push(recentSection);
  }

  return sections;
};

export const findFolderInTree = (nodes, folderId) => {
  if (!folderId || !Array.isArray(nodes)) return null;
  for (const node of nodes) {
    if (node.id === folderId) return node;
    if (node.children?.length) {
      const found = findFolderInTree(node.children, folderId);
      if (found) return found;
    }
  }
  return null;
};

export const findAncestorIds = (nodes, folderId, path = []) => {
  if (!folderId || !Array.isArray(nodes)) return null;
  for (const node of nodes) {
    if (node.id === folderId) return path;
    if (node.children?.length) {
      const found = findAncestorIds(node.children, folderId, [...path, node.id]);
      if (found) return found;
    }
  }
  return null;
};

export const findSectionForFolder = (nodes, folderId) => {
  if (!folderId || !Array.isArray(nodes)) return null;
  for (const section of nodes) {
    if (section.id === folderId) return section;
    if (section.children?.length) {
      const found = findFolderInTree(section.children, folderId);
      if (found) return section;
    }
  }
  return null;
};
